import { apiClient } from '@/lib/api';
import type {
  EjeDeTiendas,
  AjustesDeLecturas,
  CambioDeCalendario,
  DetalleDeTiendaPrixia,
  FilaDeLecturaPrixia,
  FilaDeTiendaPrixia,
  FiltroDeEstadoDeLectura,
  Lector,
  PlataformaDetectada,
  ResultadoDeFusion,
  ResumenDeLecturas,
  TiendasDeLaEmpresa,
  TipoDeLectura,
} from '../types/prixia.types';

/** Todo lo de `/api/admin/prixia` (plan 297). Sólo lo usa el panel de plataforma. */
const BASE = '/api/admin/prixia';

export const prixiaService = {
  listarTiendas: () => apiClient.get<FilaDeTiendaPrixia[]>(`${BASE}/tiendas`),

  detalle: (id: string) => apiClient.get<DetalleDeTiendaPrixia>(`${BASE}/tiendas/${id}`),

  alta: (datos: {
    nombre: string;
    url: string;
    scraperType: string;
    logoUrl: string | null;
    /** L7 ronda B: sólo si se sabe (ISO 4217). */
    moneda?: string;
    /** Reglas de la demo (343): el rubro de la tienda (id de eje). */
    ejeId?: string | null;
  }) => apiClient.post<{ storeId: string; nueva: boolean }>(`${BASE}/tiendas`, datos),

  /** L7: pregunta a la tienda cómo se lee; no guarda nada. */
  detectar: (url: string) =>
    apiClient.post<PlataformaDetectada>(`${BASE}/tiendas/detectar`, { url }),

  editar: (
    id: string,
    cambio: {
      name?: string;
      scraperType?: string;
      logoUrl?: string | null;
      visitUrl?: string;
      moneda?: string | null;
      ejeId?: string | null;
    }
  ) => apiClient.patch(`${BASE}/tiendas/${id}`, cambio),

  cambiarEstado: (id: string, isActive: boolean) =>
    apiClient.patch<{ cambiada: boolean; canceladas: number }>(`${BASE}/tiendas/${id}/estado`, {
      isActive,
    }),

  cambiarCalendario: (id: string, tipo: TipoDeLectura, cambio: CambioDeCalendario) =>
    apiClient.put<{ id: string; nextRunAt: string | null }>(
      `${BASE}/tiendas/${id}/calendario/${tipo}`,
      cambio
    ),

  leer: (id: string, type: TipoDeLectura) =>
    apiClient.post<{ lecturaId: string; tipo: TipoDeLectura; sumada: boolean }>(
      `${BASE}/tiendas/${id}/leer`,
      { type }
    ),

  fusionar: (id: string, tenantId: string) =>
    apiClient.post<ResultadoDeFusion>(`${BASE}/tiendas/${id}/fusionar`, { tenantId }),

  lecturas: (
    filtro: { estado?: FiltroDeEstadoDeLectura; storeId?: string; dias?: number } = {}
  ) => {
    const q = new URLSearchParams();
    if (filtro.estado && filtro.estado !== 'TODAS') q.set('estado', filtro.estado);
    if (filtro.storeId) q.set('storeId', filtro.storeId);
    if (filtro.dias) q.set('dias', String(filtro.dias));
    const sufijo = q.toString();
    return apiClient.get<{ resumen: ResumenDeLecturas; lecturas: FilaDeLecturaPrixia[] }>(
      `${BASE}/lecturas${sufijo ? `?${sufijo}` : ''}`
    );
  },

  cancelarLectura: (id: string) =>
    apiClient.post<{ cancelada: boolean }>(`${BASE}/lecturas/${id}/cancelar`),

  ajustes: () => apiClient.get<AjustesDeLecturas>(`${BASE}/ajustes`),

  guardarAjustes: (cambio: Partial<Omit<AjustesDeLecturas, 'lecturasSimultaneas'>>) =>
    apiClient.put<AjustesDeLecturas>(`${BASE}/ajustes`, cambio),

  lectores: () => apiClient.get<Lector[]>(`${BASE}/lectores`),

  /** Reglas de la demo (343): los rubros de las tiendas (`/api/admin/ejes`, sólo plataforma). */
  ejes: () => apiClient.get<EjeDeTiendas[]>('/api/admin/ejes'),

  crearEje: (datos: { nombre: string }) => apiClient.post<EjeDeTiendas>('/api/admin/ejes', datos),

  editarEje: (
    id: string,
    cambio: { nombre?: string; orden?: number; activo?: boolean; tiendaSugeridaId?: string | null }
  ) => apiClient.patch<EjeDeTiendas>(`/api/admin/ejes/${id}`, cambio),

  tiendasDeLaEmpresa: (tenantId: string) =>
    apiClient.get<TiendasDeLaEmpresa>(`${BASE}/empresas/${tenantId}`),

  guardarEmpresa: (
    tenantId: string,
    cambio: { storeIds?: string[]; readingMonitor?: boolean; readNowDailyQuota?: number | null }
  ) => apiClient.put<TiendasDeLaEmpresa>(`${BASE}/empresas/${tenantId}`, cambio),
};
